/*---------------React & Libraries-----------------*/
import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { observer } from "mobx-react-lite";

/*---------------Material UI Components-----------*/
import {
  Box,
  Button,
  Grid,
  InputBase,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/*---------------Types & Models-------------------*/
import {
  CustomerList,
  CustomerSearchFormValues,
} from "../../app/models/Customer";

/*---------------Components-----------------------*/
import NumberInput from "../../app/shared/common/forms/NumberInput";
import SelectInput from "../../app/shared/common/forms/SelectInput";
import TextInput from "../../app/shared/common/forms/TextInput";

/*---------------Stores---------------------------*/
import { useStore } from "../../app/stores/store";

interface CustomerSearchFormProps {
  onSubmit: (values: CustomerSearchFormValues) => void;
  handleReset: () => void;
}

const statusOptions = [
  { name: "Active", value: true },
  { name: "Inactive", value: false },
];

// Check valid outlet name
const isValidOutletName = (name: string | null | undefined): name is string => {
  if (!name || name.trim() === "") return false;
  return /[a-zA-Z0-9]/.test(name);
};

// Filter outlets by search text
const filterOutlets = (
  outlets: CustomerList[],
  search: string,
): CustomerList[] => {
  const query = search.trim().toLowerCase();
  if (!query) return outlets;
  return outlets.filter((o) => o.name.toLowerCase().includes(query));
};

// Custom Outlet Dropdown
interface OutletDropdownProps {
  outlets: CustomerList[];
  value: string;
  onChange: (name: string) => void;
}

const OutletDropdown: React.FC<OutletDropdownProps> = ({
  outlets,
  value,
  onChange,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside the component
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // When the external value is cleared (e.g. Reset), clear the input text too
  useEffect(() => {
    if (!value) setInputText("");
  }, [value]);

  const validOutlets = outlets.filter((o) => isValidOutletName(o.name));
  const filtered = filterOutlets(validOutlets, inputText);

  const handleSelect = (outlet: CustomerList) => {
    onChange(outlet.name);
    setInputText(outlet.name);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    // Clear the Formik value when user starts typing freely
    if (value) onChange("");
    setOpen(true);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClear = () => {
    setInputText("");
    onChange("");
    setOpen(false);
  };

  return (
    <Box ref={containerRef} sx={{ position: "relative", width: "100%" }}>
      {/* ── Input Row ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: `1px solid`,
          borderColor: open
            ? theme.palette.primary.main
            : theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.23)"
              : "rgba(0,0,0,0.23)",
          borderRadius: 1,
          px: 1,
          height: 40,
          bgcolor: "background.paper",
          transition: "border-color 0.2s",
          "&:hover": {
            borderColor: theme.palette.text.primary,
          },
        }}
      >
        <InputBase
          value={inputText}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          placeholder="Search Outlet"
          sx={{ flex: 1, fontSize: 14 }}
          inputProps={{ "aria-label": "Search outlet by name" }}
        />

        {/* Clear button — shown only when there is text */}
        {inputText && (
          <Box
            component="span"
            onClick={handleClear}
            sx={{
              cursor: "pointer",
              color: "text.secondary",
              fontSize: 16,
              lineHeight: 1,
              px: 0.5,
              userSelect: "none",
              "&:hover": { color: "text.primary" },
            }}
            aria-label="Clear outlet search"
          >
            ✕
          </Box>
        )}

        {/* Chevron toggle icon */}
        <Box
          component="span"
          onClick={handleToggle}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
          aria-label={open ? "Collapse outlet list" : "Expand outlet list"}
        >
          {open ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </Box>
      </Box>

      {/* ── Dropdown List ── */}
      {open && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: theme.zIndex.modal,
            maxHeight: 220,
            overflowY: "auto",
            borderRadius: 1,
            // Thin scrollbar on desktop
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "divider",
              borderRadius: 3,
            },
          }}
        >
          {filtered.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ px: 2, py: 1.5, color: "text.secondary" }}
            >
              No outlet found
            </Typography>
          ) : (
            filtered.map((outlet) => (
              <Box
                key={outlet.id}
                onClick={() => handleSelect(outlet)}
                sx={{
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  fontSize: 14,
                  bgcolor:
                    value === outlet.name ? "action.selected" : "transparent",
                  "&:hover": { bgcolor: "action.hover" },
                  // Highlight matching text segment
                  borderBottom: `1px solid`,
                  borderColor: "divider",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <HighlightedText text={outlet.name} highlight={inputText} />
              </Box>
            ))
          )}
        </Paper>
      )}
    </Box>
  );
};

// Highlighted Text Helper
const HighlightedText: React.FC<{ text: string; highlight: string }> = ({
  text,
  highlight,
}) => {
  if (!highlight.trim()) {
    return <span>{text}</span>;
  }
  const regex = new RegExp(
    `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <strong key={i} style={{ color: "inherit" }}>
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  );
};

// Main Form
const CustomerSearchForm: React.FC<CustomerSearchFormProps> = observer(
  ({ onSubmit, handleReset }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { customerStore } = useStore();
    const { allCustomers, fetchAllCustomersForDropdown } = customerStore;

    useEffect(() => {
      fetchAllCustomersForDropdown();
    }, [fetchAllCustomersForDropdown]);

    const initialValues: CustomerSearchFormValues = {
      name: "",
      mobile: "",
      email: "",
      isActive: "",
    };

    return (
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {({ resetForm, isSubmitting, values, setFieldValue }) => (
          <Form>
            <Box
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              <Grid container spacing={2} alignItems="flex-end">
                {/* ── Outlet Name ── */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      component="label"
                      variant="body2"
                      sx={{
                        display: "block",
                        mb: 0.5,
                        fontWeight: 500,
                        color: "text.primary",
                      }}
                    >
                      Outlet Name
                    </Typography>
                    <OutletDropdown
                      outlets={allCustomers}
                      value={values.name}
                      onChange={(name) => setFieldValue("name", name)}
                    />
                  </Box>
                </Grid>

                {/* ── Mobile Number ── */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <NumberInput
                    name="mobile"
                    label="Outlet Number"
                    maxDigits={10}
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* ── Email ── */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextInput
                    name="email"
                    label="Email ID"
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* ── Status ── */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <SelectInput
                    name="isActive"
                    label="Status"
                    nameKey="name"
                    valueKey="value"
                    options={statusOptions}
                    addOption
                    placeholder="Status"
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* ── Action Buttons ── */}
                <Grid size={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: { xs: "center", sm: "flex-end" },
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        resetForm();
                        handleReset();
                      }}
                      size="small"
                      fullWidth={isSmallScreen}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="small"
                      fullWidth={isSmallScreen}
                      disabled={isSubmitting}
                    >
                      Search
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Form>
        )}
      </Formik>
    );
  },
);

export default CustomerSearchForm;
